import type { MouseEvent } from 'react';

import { Link, VStack } from '@chakra-ui/react';
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { formatDocumentName } from '#utils/format-document-name';

export interface SubpagesLinksListProps {
  parentPath: string;
  children: Array<{ id: string; path: string; label?: string }>;
  onNavigateToDocument?: (path: string) => void;
}

/** Link list for child documents (no heading or chrome). */
export function SubpagesLinksList({
  parentPath,
  children,
  onNavigateToDocument,
}: SubpagesLinksListProps) {
  const getRelativePath = useCallback(
    (childPath: string) => {
      if (!parentPath) return childPath;

      if (childPath === parentPath) return '';

      const prefix = parentPath + '/';
      return childPath.startsWith(prefix)
        ? childPath.slice(prefix.length)
        : childPath;
    },
    [parentPath],
  );

  const formatChildLabel = useCallback(
    (childPath: string) => {
      const relative = getRelativePath(childPath);
      const formatted = formatDocumentName(relative).join(' › ');
      return formatted || relative || childPath;
    },
    [getRelativePath],
  );

  const handleNavigate = useCallback(
    (path: string) => (e: MouseEvent) => {
      if (typeof onNavigateToDocument !== 'function') return;

      e.preventDefault();
      onNavigateToDocument(path);
    },
    [onNavigateToDocument],
  );

  return (
    <VStack align="stretch" gap={1}>
      {children.map((child) => (
        <Link
          key={child.id}
          asChild
          fontSize="sm"
          color="blue.600"
          _hover={{ textDecoration: 'underline' }}
        >
          <RouterLink
            to={`/documents/${child.path}`}
            onClick={handleNavigate(child.path)}
          >
            {formatChildLabel(child.path)}
          </RouterLink>
        </Link>
      ))}
    </VStack>
  );
}
